import { z } from 'zod';
import type { ApiTokenScope } from './api-tokens';
import { todayIso } from './format';
import { idSchema } from './ids';
import { fromCents, sumCents, toCents } from './money';
import { pathParams, type ResponseOf, type RouteDef, routes } from './routes';
import type { Support } from './support';
import { type ProjectTimesheets, summarizeSheet, type SummaryColumn } from './timesheets';
import { turnoverAnnotationInputSchema, summarizeTurnover } from './turnover';

/**
 * MCP tools (FR-MCP-001…010, ADR 0008). Names follow the PMAI MCP server, so existing Claude
 * flows keep working. Every tool is a thin mapping onto API routes: the API call runs with the
 * caller's token through the same validation, permission checks and services as the web app —
 * a tool never reads or writes data itself. Arguments are the routes' own schemas.
 */

export interface McpRequest {
  params?: Record<string, string>;
  query?: object;
  body?: unknown;
}

/** One API call as the token's user; a refused call throws (the tool answers with the error). */
export type McpCall = <R extends RouteDef>(route: R, req?: McpRequest) => Promise<ResponseOf<R>>;

export interface McpTool {
  name: string;
  description: string;
  /** `write` tools are not offered to read tokens (and the API refuses their calls anyway). */
  scope: ApiTokenScope;
  input: z.ZodObject;
  run: (args: Record<string, unknown>, call: McpCall) => Promise<unknown>;
}

type Args = Record<string, unknown>;

/** A tool argument that is a missing path parameter — answered before any API call. */
export class McpArgumentError extends Error {}

const scopeOf = (route: RouteDef): ApiTokenScope => (route.method === 'GET' ? 'read' : 'write');
const shapeOf = (schema: z.ZodType | undefined) => (schema instanceof z.ZodObject ? schema.shape : {});

interface Binding {
  /** Path parameter → argument name (`:id` → `projectId`); default the parameter's own name. */
  params?: Record<string, string>;
}

/** Path parameters from the arguments; the remaining arguments. */
function split(route: RouteDef, binding: Binding, args: Args) {
  const params: Record<string, string> = {};
  const rest: Args = { ...args };
  for (const p of pathParams(route.path)) {
    const arg = binding.params?.[p] ?? p;
    const value = args[arg];
    if (value === undefined || value === null || value === '')
      throw new McpArgumentError(`Trūkst argumenta "${arg}".`);
    params[p] = String(value);
    delete rest[arg];
  }
  return { params, rest };
}

const paramShape = (route: RouteDef, binding: Binding) =>
  Object.fromEntries(pathParams(route.path).map((p) => [binding.params?.[p] ?? p, idSchema]));

interface RouteTool<R extends RouteDef> extends Binding {
  /** Replaces the route's own body/query schema as the tool's arguments. */
  input?: z.ZodObject;
  /** Arguments (without path parameters) → query or body; default as they are. */
  request?: (args: Args) => object;
  /** What the tool answers; default the API response. */
  pick?: (res: ResponseOf<R>, args: Args) => unknown;
}

/** One route = one tool: arguments are its path parameters plus its query (GET) or body. */
function fromRoute<R extends RouteDef>(
  name: string,
  description: string,
  route: R,
  o: RouteTool<R> = {},
): McpTool {
  const own = o.input ?? (route.method === 'GET' ? route.query : route.body);
  return {
    name,
    description,
    scope: scopeOf(route),
    input: z.object({ ...paramShape(route, o), ...shapeOf(own) }),
    run: async (args, call) => {
      const { params, rest } = split(route, o, args);
      const payload = o.request ? o.request(rest) : rest;
      const res = await call(
        route,
        route.method === 'GET' ? { params, query: payload } : { params, body: payload },
      );
      return o.pick ? o.pick(res, args) : res;
    },
  };
}

/**
 * PMAI-style partial update: reads the record, applies only the given fields and saves it with
 * the version just read (unless one is given) — the same update route as the edit form.
 */
function partialUpdate(
  name: string,
  description: string,
  read: RouteDef,
  update: RouteDef & { body: z.ZodObject },
  binding: Binding,
): McpTool {
  return {
    name,
    description: `${description} Maina tikai norādītos laukus.`,
    scope: 'write',
    input: z.object({ ...paramShape(update, binding), ...update.body.partial().shape }),
    run: async (args, call) => {
      const { params, rest } = split(update, binding, args);
      const current = (await call(read, { params })) as object;
      return call(update, { params, body: { ...current, ...rest } });
    },
  };
}

/** "Financial Summary" per year and in total for Plan and Fact (FR-TS-018), money as strings. */
function financials(sheets: ProjectTimesheets) {
  const money = (c: SummaryColumn) => ({
    year: c.year,
    hours: c.hours,
    expenses: fromCents(c.expenses),
    invoicedAdvance: fromCents(c.invoicedAdvance),
    invoiced: fromCents(c.invoiced),
    revenue: fromCents(c.revenue),
    balance: fromCents(c.balance),
    toInvoice: fromCents(c.toInvoice),
    invoicedPct: c.invoicedPct,
    profit: fromCents(c.profit),
    margin: c.margin,
  });
  return { plan: summarizeSheet(sheets.plan).map(money), fact: summarizeSheet(sheets.fact).map(money) };
}

/** Active support records and the monthly recurring revenue (FR-MCP-006). */
export function summarizeSupport(rows: readonly Support[]) {
  const active = rows.filter((r) => r.isActive);
  return {
    total: rows.length,
    active: active.length,
    needsReview: rows.filter((r) => r.needsReview).length,
    monthlyRecurringRevenue: fromCents(sumCents(active.map((r) => toCents(r.monthlyFee)))),
  };
}

const r = routes;
const monthArgs = turnoverAnnotationInputSchema.pick({ year: true, month: true });
const annotations = (want: 'flagged' | 'comment') =>
  fromRoute(
    want === 'flagged' ? 'get_turnover_flags' : 'get_turnover_comments',
    want === 'flagged'
      ? 'Mēneša "zem jautājuma" atzīmes pa projektiem. Filtrs: projectId.'
      : 'Mēneša apgrozījuma komentāri pa projektiem. Filtrs: projectId.',
    r.turnover.get,
    {
      input: monthArgs.extend({ projectId: idSchema.optional() }),
      request: ({ year, month }) => ({ year, month }),
      pick: (res, { projectId }) =>
        res.annotations.filter(
          (a) =>
            (want === 'flagged' ? a.flagged : a.comment !== '') && (!projectId || a.projectId === projectId),
        ),
    },
  );
const annotate = (want: 'flagged' | 'comment') =>
  fromRoute(
    want === 'flagged' ? 'set_turnover_flag' : 'set_turnover_comment',
    want === 'flagged'
      ? 'Uzstāda vai noņem projekta mēneša "zem jautājuma" atzīmi (month 1–12).'
      : 'Uzstāda projekta mēneša apgrozījuma komentāru (month 1–12; tukšs — noņem).',
    r.turnover.annotate,
    {
      params: { id: 'projectId' },
      input:
        want === 'flagged'
          ? monthArgs.extend({ flagged: z.boolean() })
          : monthArgs.extend({ comment: z.string().max(2000) }),
    },
  );

/** The sections the caller may read; one the role may not see is left out (FR-MCP-010). */
async function summary(call: McpCall) {
  const today = todayIso();
  const [year, month] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  const optional = <T>(p: Promise<T>) => p.catch(() => undefined);
  const [projects, tasks, customers, resources, support, time, turnover] = await Promise.all([
    optional(call(r.projects.list, { query: { limit: 1000 } })),
    optional(call(r.tasks.list, { query: { status: 'all' } })),
    optional(call(r.customers.list, { query: { limit: 1 } })),
    optional(call(r.resources.list)),
    optional(call(r.support.list)),
    optional(call(r.timeOverview.get, { query: { from: `${today.slice(0, 8)}01`, to: today } })),
    optional(call(r.turnover.get, { query: { year, month } })),
  ]);
  const byStatus = (rows: readonly { status: string }[]) =>
    rows.reduce<Record<string, number>>((a, p) => ({ ...a, [p.status]: (a[p.status] ?? 0) + 1 }), {});
  return {
    ...(projects && { projects: { total: projects.total, byStatus: byStatus(projects.rows) } }),
    ...(tasks && {
      tasks: {
        total: tasks.summary.count,
        open: tasks.summary.count - tasks.summary.done,
        done: tasks.summary.done,
        overdue: tasks.rows.filter((t) => !t.done && t.deadline !== null && t.deadline < today).length,
      },
    }),
    ...(customers && { customers: { total: customers.total } }),
    ...(resources && { resources: { total: resources.length } }),
    ...(support && { support: summarizeSupport(support) }),
    ...(time && { worklogs: { hoursThisMonth: time.total } }),
    ...(turnover && {
      turnover: {
        year,
        month,
        ...summarizeTurnover(
          turnover.rows,
          new Set(turnover.annotations.filter((a) => a.flagged).map((a) => a.projectId)),
        ),
      },
    }),
  };
}

const findByName = (
  name: string,
  what: 'projekts' | 'klients',
  route: typeof r.projects.list | typeof r.customers.list,
) =>
  fromRoute(
    name,
    `Meklē ${what === 'projekts' ? 'projektu' : 'klientu'} pēc nosaukuma (daļējs); atgriež ID un nosaukumu.`,
    route,
    {
      input: z.object({ name: z.string().trim().min(1).max(200) }),
      request: ({ name: q }) => ({ q, limit: 50 }),
      pick: (res) =>
        res.rows.length
          ? res.rows.map(({ id, name: n }) => ({ id, name: n }))
          : `Neviens ${what} nav atrasts.`,
    },
  );

export const MCP_TOOLS: readonly McpTool[] = [
  // ── Projekti (FR-MCP-001) ──
  fromRoute(
    'get_projects',
    'Projektu saraksts. Filtri: q (meklēšana), statuses (komatiem), customerId; kārtošana, lapošana.',
    r.projects.list,
  ),
  fromRoute('get_project', 'Projekta detaļas pēc ID.', r.projects.get),
  findByName('find_project_by_name', 'projekts', r.projects.list),
  fromRoute(
    'create_project',
    'Izveido projektu (klients — customerId; sk. find_customer_by_name).',
    r.projects.create,
  ),
  partialUpdate('update_project', 'Atjaunina projektu pēc ID.', r.projects.get, r.projects.update, {
    params: { id: 'projectId' },
  }),
  fromRoute(
    'get_project_financials',
    'Projekta finanšu kopsavilkums pa gadiem un kopā (plāns un fakts).',
    r.timesheets.ofProject,
    {
      params: { id: 'projectId' },
      pick: financials,
    },
  ),
  // ── Uzdevumi (FR-MCP-002) ──
  fromRoute(
    'get_tasks',
    'Uzdevumi. Filtri: projectId, customerId, assigneeId, status (all/open/done), from/to (termiņš).',
    r.tasks.list,
  ),
  fromRoute('get_task', 'Uzdevums pēc ID.', r.tasks.get, { params: { id: 'taskId' } }),
  fromRoute(
    'create_task',
    'Izveido uzdevumu projektam (atbildīgais — assigneeId, resursa ID).',
    r.tasks.create,
  ),
  partialUpdate('update_task', 'Atjaunina uzdevumu pēc ID (arī done).', r.tasks.get, r.tasks.update, {
    params: { id: 'taskId' },
  }),
  fromRoute('delete_task', 'Dzēš uzdevumu pēc ID.', r.tasks.remove, { params: { id: 'taskId' } }),
  // ── Klienti (FR-MCP-003) ──
  fromRoute(
    'get_customers',
    'Klientu saraksts. Filtri: q (meklēšana), status; kārtošana, lapošana.',
    r.customers.list,
  ),
  fromRoute('get_customer', 'Klienta detaļas pēc ID.', r.customers.get),
  findByName('find_customer_by_name', 'klients', r.customers.list),
  fromRoute('create_customer', 'Izveido klientu.', r.customers.create),
  partialUpdate('update_customer', 'Atjaunina klientu pēc ID.', r.customers.get, r.customers.update, {}),
  fromRoute('get_customer_notes', 'Klienta piezīmes (jaunākās pirmās).', r.notes.listForCustomer, {
    params: { id: 'customerId' },
  }),
  fromRoute('add_customer_note', 'Pievieno piezīmi klientam.', r.notes.createForCustomer, {
    params: { id: 'customerId' },
  }),
  // ── Projektu piezīmes (FR-MCP-004) ──
  fromRoute(
    'get_daily_notes',
    'Projekta piezīmes (jaunākās pirmās); includeArchived — arī arhivētās.',
    r.notes.listForProject,
    {
      params: { id: 'projectId' },
    },
  ),
  fromRoute('add_project_note', 'Pievieno piezīmi projektam.', r.notes.createForProject, {
    params: { id: 'projectId' },
  }),
  // ── Plāns/fakts un worklogi (FR-MCP-005) ──
  fromRoute(
    'get_project_plan_fact',
    'Projekta laika uzskaites plāns un fakts (mēneši, resursu rindas, summas).',
    r.timesheets.ofProject,
    {
      params: { id: 'projectId' },
    },
  ),
  fromRoute(
    'get_worklogs',
    'Jira worklogi. Filtri: tab (pending/approved/rejected), from, to, author, project; lapošana offset/limit.',
    r.worklogs.list,
  ),
  fromRoute(
    'get_worklog_summary',
    'Worklogu stundas periodā pa personām vai projektiem (mode) un laika grupām (group).',
    r.timeOverview.get,
  ),
  // ── Atbalsts (FR-MCP-006) ──
  fromRoute('get_support', 'Atbalsta/uzturēšanas ieraksti. Filtrs: customerId.', r.support.list),
  fromRoute(
    'get_support_summary',
    'Atbalsta kopsavilkums: ieraksti, aktīvie, jāpārskata, ikmēneša ieņēmumi (MRR).',
    r.support.list,
    {
      input: z.object({}),
      pick: summarizeSupport,
    },
  ),
  // ── Resursi (FR-MCP-007) ──
  fromRoute('get_resources', 'Resursu (darbinieku) saraksts.', r.resources.list),
  fromRoute('get_resource', 'Resurss pēc ID.', r.resources.get),
  fromRoute('create_resource', 'Izveido resursu.', r.resources.create),
  partialUpdate('update_resource', 'Atjaunina resursu pēc ID.', r.resources.get, r.resources.update, {}),
  fromRoute('delete_resource', 'Dzēš resursu pēc ID.', r.resources.remove),
  // ── Apgrozījums (FR-MCP-008) ──
  fromRoute(
    'get_turnover',
    'Mēneša apgrozījums pa projektiem (year, month 1–12; source plan/fact/both) un neizrakstītais.',
    r.turnover.get,
  ),
  annotations('flagged'),
  annotate('flagged'),
  annotations('comment'),
  annotate('comment'),
  // ── Tehnoloģijas (FR-MCP-009) ──
  fromRoute('get_technologies', 'Tehnoloģiju/produktu saraksts (ar logo).', r.technologies.list),
  fromRoute(
    'create_technology',
    'Izveido tehnoloģiju; logo — neobligāts (60×60 PNG data URL).',
    r.technologies.create,
  ),
  fromRoute(
    'update_technology',
    'Atjaunina tehnoloģijas nosaukumu un/vai logo pēc ID.',
    r.technologies.update,
  ),
  fromRoute(
    'delete_technology',
    'Dzēš tehnoloģiju pēc ID (noņem to arī no projektiem).',
    r.technologies.remove,
  ),
  fromRoute(
    'set_project_technologies',
    'Uzstāda (aizvieto) projekta tehnoloģijas: technologyIds.',
    r.technologies.setForProject,
    {
      params: { id: 'projectId' },
    },
  ),
  fromRoute(
    'copy_project_technologies',
    'Pievieno projekta tehnoloģijas citiem projektiem (projectIds; esošās paliek).',
    r.technologies.copyToProjects,
    {
      params: { id: 'fromProjectId' },
    },
  ),
  // ── Kopsavilkums (FR-MCP-010) ──
  {
    name: 'get_summary',
    description:
      'Kopsavilkums: projekti, uzdevumi, klienti, resursi, atbalsts (MRR), worklogu stundas un apgrozījums šomēnes.',
    scope: 'read',
    input: z.object({}),
    run: (_args, call) => summary(call),
  },
];

/** The tool's arguments as JSON Schema (what `tools/list` shows) — from the same zod schemas. */
export function mcpInputJsonSchema(tool: McpTool): Record<string, unknown> {
  const { $schema: _, ...schema } = z.toJSONSchema(tool.input, { io: 'input', unrepresentable: 'any' });
  return schema;
}
